/**
 * Image Search Agent - Suggestions Section
 * Section 3: Suggestions for best performance
 */
import { PromptSectionBase } from '../../../base/systemPrompt/PromptSectionBase';
import { IMAGE_SEARCH_PROMPT_KEYS } from './variables';
class ImageSearchSuggestionsSection extends PromptSectionBase {
    constructor() {
        super(...arguments);
        this.keywordsKey = IMAGE_SEARCH_PROMPT_KEYS.suggestions;
        this.fallbackContent = `## Best Practices

### Source Selection Guidelines

**For company logos** (search in this order):
1. **Brandfetch** (first priority) - search_brandfetch → retrieve_brandfetch
2. **Presentation search** (if documentId provided) - search_presentation_images → retrieve_presentation_image
3. **Wikimedia** (fallback) - search_wikimedia → retrieve_wikimedia

**For other media** (headshots, products, general images):
1. **Presentation search** (if documentId provided)
2. **Wikimedia** - search_wikimedia → retrieve_wikimedia

### Structured Preferences (preferredShape / preferredBackground)

Requirements may include optional structured preferences:

**preferredShape:**
- \`"square"\` → icon-only, ~1:1 aspect ratio (for grid cells, table cells)
- \`"horizontal"\` → wide logo with text (for banners, headers, two-column grids)
- \`"any"\` or omitted → no preference

**preferredBackground:**
- \`"light_bg"\` → logo placed on light/white bg → select dark/colored variants (dark_logo, dark_icon)
- \`"dark_bg"\` → logo placed on dark bg → select light/white variants (light_logo, light_icon)
- \`"any"\` or omitted → no preference

**When you can't match a preference:**
- Still return the best available logo — do NOT return nothing
- Note the mismatch in your asset note (e.g., "only square icon available, no horizontal variant")
- The system will automatically flag mismatches to the orchestrator in the response

### Logo Selection Workflow

1. **Call retrieve_brandfetch** - fetches ALL available logo variants automatically
   
2. **Select based on preferredBackground or variant name:**
   - \`preferredBackground: "light_bg"\` or "dark_logo" / "dark_icon" → colorful/dark logos, best for LIGHT backgrounds
   - \`preferredBackground: "dark_bg"\` or "light_logo" / "light_icon" → white/light logos, best for DARK backgrounds
   - If not specified, assume light background (most common)
   
3. **Select based on preferredShape:**
   - \`preferredShape: "square"\` → prefer icon/symbol variants (no text)
   - \`preferredShape: "horizontal"\` → prefer logo variants (with text, wide)
   - Check the metadata.shape field to verify: "square", "horizontal", or "vertical"

4. **Submit the best asset ID(s)**

### logoOnly Parameter
- **logoOnly: true** (default) - fast retrieval, logo images only
- **logoOnly: false** - also retrieves brand colors/fonts

### Asset Notes
Write brief notes describing:
- What the asset is
- Variant type and intended use
- Any mismatch with requested preferences (shape or background)`;
    }
}
const suggestionsSection = new ImageSearchSuggestionsSection();
/**
 * Get the suggestions section.
 * Uses Keywords AI if available, otherwise falls back to code.
 */
export function getSuggestionsSection() {
    return suggestionsSection.getContent();
}
/**
 * Export constant for backwards compatibility
 */
export const IMAGE_SEARCH_SUGGESTIONS = suggestionsSection.getFallbackContent();
